import { IonButton } from '@ionic/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { DocDataByMark } from '../../../api/hooks/delivery/useDeliveryStatusByMark';
import { PATHS } from '../../../navigation/data';
import {
  canConfirmDeliveryReturn,
  canConfirmOutcome,
  canRegisterRejection,
  canRegisterTransfer,
  getConfirmOutcomeButtonCopy,
} from '../../../services/utils/deliveryStatusUtils';
import { DeliveryStatusGsisDoc } from './DeliveryStatusGsisDoc';
import { DeliveryStatusSteps } from './DeliveryStatusSteps';

interface Props {
  docData: DocDataByMark;
}

export const DeliveryDocDetails = ({ docData }: Props) => {
  const { data, extraData, errorCode } = docData;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const markForNextStep = useMemo(() => extraData?.doc?.mark || 'create', [extraData?.doc?.mark]);

  const getErrorCode = useCallback(() => {
    if (errorCode && !extraData?.doc) {
      return (
        <div className="m-t-16 m-b-16 flex-center-center-column-nowrap width-100">
          <div className="boldText4 text-center text-danger text-with-new-line max-width-900">
            {t(`API_ERRORS.${errorCode}`)}
          </div>
        </div>
      );
    }

    return null;
  }, [errorCode, extraData?.doc, t]);

  const getCurrentStatus = useCallback(() => {
    if (data) {
      return (
        <div className="margin-b-16 flex-center-center-column-nowrap width-100">
          <div className="boldText6">{t('DELIVERY.DELIVERY_STATUS')}</div>
          <div className="margin-t-16 simpleBorder p-12">
            <div className="normalText5 m-b-6">
              <b>{t('DELIVERY.NEW_DELIVERY_MARK_PLACEHOLDER')}</b>: {data?.invoiceMark}
            </div>
            <div className="normalText5">
              <b>{t('DELIVERY.DELIVERY_STATUS_CURRENT')}</b>: {t(`DELIVERY.STATUS.${data?.status}`)}
            </div>
          </div>
        </div>
      );
    }
  }, [data, t]);

  const getGsisDocData = useCallback(() => {
    if (extraData?.doc) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryStatusGsisDoc doc={extraData?.doc} />
        </div>
      );
    }
    return null;
  }, [extraData?.doc]);

  const getSteps = useCallback(() => {
    if (data?.lifecycleHistory) {
      return (
        <div className="m-b-32 flex-center-center-column-nowrap width-100">
          <DeliveryStatusSteps lifecycleHistory={data?.lifecycleHistory} />
        </div>
      );
    }
  }, [data?.lifecycleHistory]);

  const getRegisterTransferButton = useCallback(() => {
    if (canRegisterTransfer(data, extraData?.doc)) {
      const goToRegisterTransfer = () => navigate(`${PATHS.DELIVERIES}/register-transfer/${markForNextStep}`);

      return (
        <IonButton mode="md" className="width-300px" onClick={goToRegisterTransfer}>
          {t('DELIVERY.NEW_DELIVERY_RECTANGLE')}
        </IonButton>
      );
    }
  }, [data, extraData?.doc, navigate, t, markForNextStep]);

  const getConfirmOutcomeButton = useCallback(() => {
    if (canConfirmOutcome(extraData?.isIssuer, data, extraData?.doc)) {
      const goToConfirmDelivery = () => navigate(`${PATHS.DELIVERIES}/confirm-outcome/${markForNextStep}`);

      const label = getConfirmOutcomeButtonCopy(extraData?.isIssuer);

      return (
        <IonButton mode="md" className="width-300px ion-info-button" fill="outline" onClick={goToConfirmDelivery}>
          {t(`DELIVERY.${label}`)}
        </IonButton>
      );
    }
  }, [data, extraData?.doc, extraData?.isIssuer, navigate, t, markForNextStep]);

  const getRejectDeliveryButton = useCallback(() => {
    if (canRegisterRejection(extraData?.isIssuer, data, extraData?.doc)) {
      const goToRegisterTransfer = () => navigate(`${PATHS.DELIVERIES}/reject-delivery/${markForNextStep}`);

      return (
        <IonButton mode="md" className="width-300px ion-delete-button" fill="outline" onClick={goToRegisterTransfer}>
          {t('DELIVERY.REJECT_DELIVERY_TITLE')}
        </IonButton>
      );
    }
  }, [data, extraData?.doc, extraData?.isIssuer, navigate, t, markForNextStep]);

  const getConfirmReturnButton = useCallback(() => {
    if (canConfirmDeliveryReturn(extraData?.isIssuer, data, extraData?.doc)) {
      const goToConfirmReturn = () => navigate(`${PATHS.DELIVERIES}/confirm-return/${markForNextStep}`);

      return (
        <IonButton mode="md" className="width-300px ion-delete-button" fill="outline" onClick={goToConfirmReturn}>
          {t('DELIVERY.CONFIRM_RETURN_RECTANGLE')}
        </IonButton>
      );
    }
  }, [data, extraData?.doc, extraData?.isIssuer, navigate, t, markForNextStep]);

  const getActionButtons = useCallback(() => {
    if (
      canRegisterTransfer(data, extraData?.doc) ||
      canConfirmOutcome(extraData?.isIssuer, data, extraData?.doc) ||
      canRegisterRejection(extraData?.isIssuer, data, extraData?.doc) ||
      canConfirmDeliveryReturn(extraData?.isIssuer, data, extraData?.doc)
    ) {
      return (
        <div className="width-100 flex-center-center-row-wrap gap-12 m-b-16">
          {getRegisterTransferButton()}
          {getConfirmOutcomeButton()}
          {getRejectDeliveryButton()}
          {getConfirmReturnButton()}
        </div>
      );
    }
  }, [
    data,
    extraData?.doc,
    extraData?.isIssuer,
    getConfirmOutcomeButton,
    getConfirmReturnButton,
    getRegisterTransferButton,
    getRejectDeliveryButton,
  ]);

  return (
    <div className="width-100 flex-center-start-column-nowrap">
      {getErrorCode()}
      {getCurrentStatus()}
      {getActionButtons()}
      {getSteps()}
      {getGsisDocData()}
    </div>
  );
};
